# ATS Screening Questions

This implementation option is recommended if your software already has a mechanism for creating and managing custom questions.
The [`createApplicationQuestionnaire` mutation] lets you create questionnaires in the structure expected by the SEEK API and returns an ID that can be specified when posting a job ad.

```mermaid Questionnaire flow
sequenceDiagram
  participant H as Hirer
  participant P as Partner
  participant G as graphql.seek.com

  H->>P: Create questionnaire
  P->>G: Map questionnaire to SEEK structure
  G-->>P: Questionnaire ID
  P->>G: Post job ad with questionnaire ID
```

## Operation

The [`createApplicationQuestionnaire` mutation] creates a new questionnaire.

The SEEK API provides 3 question types for custom questions:

- `FreeText` presents the candidate with a free text input labelled with the question’s `questionHtml`.
  They must input at least one character before submitting their application.

- `SingleSelect` presents the candidate with a set of mutually exclusive choices.
  They must select a single choice before submitting their application.

  SEEK renders these questions either as radio buttons or a dropdown list, depending on the number of choices.

- `MultiSelect` presents the candidate with a set of non-exclusive choices.
  They must select at least one choice before submitting their application.

  SEEK renders these questions as a list of checkboxes.

If you manage individual questionnaire components in your software,
supply a unique reference `value` for each [`ApplicationQuestionnaireComponent`] during questionnaire creation.
You can then retrieve `responses.*.component.value` from the submission to correlate each response back to its corresponding component.

If you manage questionnaires as a holistic collection in your software,
retrieve `questionnaire.id` to correlate the submission back to the questionnaire ID.

```graphql
mutation ($input: CreateApplicationQuestionnaireInput!) {
  createApplicationQuestionnaire(input: $input) {
    applicationQuestionnaire {
      id {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "applicationQuestionnaire": {
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "components": [
        {
          "componentTypeCode": "Question",
          "question": {
            "componentTypeCode": "Question",
            "value": "question1",
            "questionHtml": "Tell us about your relevant mock data experience",
            "responseTypeCode": "FreeText"
          }
        },
        {
          "componentTypeCode": "PrivacyConsent",
          "privacyConsent": {
            "componentTypeCode": "PrivacyConsent",
            "value": "consent1",
            "descriptionHtml": "Do you agree to our privacy policy?",
            "privacyPolicyUrl": {
              "url": "https://au.seek.com/privacy"
            }
          }
        }
      ]
    }
  }
}
```

The [`ApplicationQuestionnaire`] object is immutable.
If your software supports mutable questionnaires,
you may snapshot its latest state by creating a new [`ApplicationQuestionnaire`] at the time of posting.

For detailed information on all available fields, refer to the [`CreateApplicationQuestionnaireInput`] schema definition.

You can use our interactive builder to explore the [`ApplicationQuestionnaire`] components supported by the SEEK API.

import QuestionnaireBuilder from './builder';

<QuestionnaireBuilder />

[`createapplicationquestionnaire` mutation]: /schema/mutation/createApplicationQuestionnaire
[`applicationquestionnaire`]: /schema/named-type/ApplicationQuestionnaire
[`createapplicationquestionnaireinput`]: /schema/named-type/CreateApplicationQuestionnaireInput
[`applicationquestionnairecomponent`]: /schema/named-type/ApplicationQuestionnaireComponent
